import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { Funnel, FunnelState } from '../types/funnel.types';
import { FunnelSection, SectionColumn } from '../types/section.types';
import { FunnelElement, Breakpoint, ElementStyles } from '../types/element.types';

/**
 * Helper function to create a new empty funnel
 */
const createEmptyFunnel = (): Funnel => ({
  id: uuidv4(),
  name: 'Untitled Funnel',
  metadata: {
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  sections: [],
});

/**
 * Helper function to find an element by ID in the funnel
 */
const findElement = (
  sections: FunnelSection[],
  elementId: string
): { element: FunnelElement; sectionId: string; columnId: string } | null => {
  for (const section of sections) {
    for (const column of section.columns) {
      const element = column.elements.find((el) => el.id === elementId);
      if (element) {
        return { element, sectionId: section.id, columnId: column.id };
      }
    }
  }
  return null;
};

/**
 * Funnel Store Actions Interface
 */
interface FunnelActions {
  // Funnel actions
  setFunnel: (funnel: Funnel) => void;
  updateFunnelName: (name: string) => void;
  resetFunnel: () => void;
  
  // Section actions
  addSection: (section: FunnelSection) => void;
  deleteSection: (sectionId: string) => void;
  duplicateSection: (sectionId: string) => void;
  reorderSections: (startIndex: number, endIndex: number) => void;
  updateSection: (sectionId: string, updates: Partial<FunnelSection>) => void;
  toggleSectionCollapse: (sectionId: string) => void;
  
  // Column actions
  addColumn: (sectionId: string, column: SectionColumn) => void;
  deleteColumn: (sectionId: string, columnId: string) => void;
  
  // Element actions
  addElement: (sectionId: string, columnId: string, element: FunnelElement, index?: number) => void;
  addElementToNestedColumn: (parentElementId: string, nestedColumnId: string, element: FunnelElement) => void;
  deleteElement: (elementId: string) => void;
  duplicateElement: (elementId: string) => void;
  updateElement: (elementId: string, updates: Partial<FunnelElement>) => void;
  updateElementStyles: (elementId: string, styles: Partial<ElementStyles>) => void;
  updateElementResponsiveStyles: (elementId: string, breakpoint: 'tablet' | 'mobile', styles: Partial<ElementStyles>) => void;
  moveElement: (elementId: string, targetSectionId: string, targetColumnId: string, targetIndex: number) => void;
  
  // Selection actions
  selectElement: (elementId: string | null) => void;
  selectSection: (sectionId: string | null) => void;
  
  // Viewport actions
  setBreakpoint: (breakpoint: Breakpoint) => void;
  
  // History actions (undo/redo)
  undo: () => void;
  redo: () => void;
  saveToHistory: () => void;
  
  // Drag state
  setIsDragging: (isDragging: boolean) => void;
  
  // Preview mode
  setPreviewMode: (isPreview: boolean) => void;
}

/**
 * Main Funnel Store
 */
export const useFunnelStore = create<FunnelState & FunnelActions>((set, get) => ({
  // Initial state
  funnel: createEmptyFunnel(),
  selectedElementId: null,
  selectedSectionId: null,
  currentBreakpoint: 'desktop',
  history: [],
  historyIndex: -1,
  isDragging: false,
  isPreviewMode: false,

  // Funnel actions
  setFunnel: (funnel) => {
    set({ funnel, history: [funnel], historyIndex: 0 });
  },

  updateFunnelName: (name) => {
    set((state) => ({
      funnel: {
        ...state.funnel,
        name,
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
    get().saveToHistory();
  },

  resetFunnel: () => {
    const newFunnel = createEmptyFunnel();
    set({ funnel: newFunnel, history: [newFunnel], historyIndex: 0, selectedElementId: null, selectedSectionId: null });
  },

  // Section actions
  addSection: (section) => {
    set((state) => ({
      funnel: {
        ...state.funnel,
        sections: [...state.funnel.sections, section],
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
    get().saveToHistory();
  },

  deleteSection: (sectionId) => {
    set((state) => ({
      funnel: {
        ...state.funnel,
        sections: state.funnel.sections.filter((s) => s.id !== sectionId),
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
      selectedSectionId: state.selectedSectionId === sectionId ? null : state.selectedSectionId,
    }));
    get().saveToHistory();
  },

  duplicateSection: (sectionId) => {
    const state = get();
    const section = state.funnel.sections.find((s) => s.id === sectionId);
    if (!section) return;

    const duplicatedSection: FunnelSection = {
      ...section,
      id: uuidv4(),
      name: `${section.name} (Copy)`,
      columns: section.columns.map((col) => ({
        ...col,
        id: uuidv4(),
        elements: col.elements.map((el) => ({
          ...el,
          id: uuidv4(),
        })) as FunnelElement[],
      })),
    };

    const sectionIndex = state.funnel.sections.findIndex((s) => s.id === sectionId);
    const newSections = [...state.funnel.sections];
    newSections.splice(sectionIndex + 1, 0, duplicatedSection);

    set({
      funnel: {
        ...state.funnel,
        sections: newSections,
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    });
    get().saveToHistory();
  },

  reorderSections: (startIndex, endIndex) => {
    set((state) => {
      const sections = [...state.funnel.sections];
      const [removed] = sections.splice(startIndex, 1);
      sections.splice(endIndex, 0, removed);

      return {
        funnel: {
          ...state.funnel,
          sections,
          metadata: {
            ...state.funnel.metadata,
            updatedAt: new Date().toISOString(),
          },
        },
      };
    });
    get().saveToHistory();
  },

  updateSection: (sectionId, updates) => {
    set((state) => ({
      funnel: {
        ...state.funnel,
        sections: state.funnel.sections.map((s) =>
          s.id === sectionId ? { ...s, ...updates } : s
        ),
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
    get().saveToHistory();
  },

  toggleSectionCollapse: (sectionId) => {
    set((state) => ({
      funnel: {
        ...state.funnel,
        sections: state.funnel.sections.map((s) =>
          s.id === sectionId ? { ...s, isCollapsed: !s.isCollapsed } : s
        ),
      },
    }));
  },

  // Column actions
  addColumn: (sectionId, column) => {
    set((state) => ({
      funnel: {
        ...state.funnel,
        sections: state.funnel.sections.map((s) =>
          s.id === sectionId
            ? { ...s, columns: [...s.columns, column] }
            : s
        ),
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
    get().saveToHistory();
  },

  deleteColumn: (sectionId, columnId) => {
    set((state) => ({
      funnel: {
        ...state.funnel,
        sections: state.funnel.sections.map((s) =>
          s.id === sectionId
            ? { ...s, columns: s.columns.filter((c) => c.id !== columnId) }
            : s
        ),
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
    get().saveToHistory();
  },

  // Element actions
  addElement: (sectionId, columnId, element, index) => {
    set((state) => ({
      funnel: {
        ...state.funnel,
        sections: state.funnel.sections.map((s) =>
          s.id === sectionId
            ? {
                ...s,
                columns: s.columns.map((c) =>
                  c.id === columnId
                    ? {
                        ...c,
                        elements:
                          index !== undefined
                            ? [
                                ...c.elements.slice(0, index),
                                element,
                                ...c.elements.slice(index),
                              ]
                            : [...c.elements, element],
                      }
                    : c
                ),
              }
            : s
        ),
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
    get().saveToHistory();
  },

  addElementToNestedColumn: (parentElementId, nestedColumnId, element) => {
    set((state) => ({
      funnel: {
        ...state.funnel,
        sections: state.funnel.sections.map((s) => ({
          ...s,
          columns: s.columns.map((c) => ({
            ...c,
            elements: c.elements.map((e) => {
              // Find the column layout element
              if (e.id === parentElementId && e.type === 'columnLayout') {
                return {
                  ...e,
                  content: {
                    ...e.content,
                    columns: e.content.columns.map((nestedCol: any) =>
                      nestedCol.id === nestedColumnId
                        ? { ...nestedCol, elements: [...nestedCol.elements, element] }
                        : nestedCol
                    ),
                  },
                } as any;
              }
              return e;
            }),
          })),
        })),
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
    get().saveToHistory();
  },

  deleteElement: (elementId) => {
    set((state) => ({
      funnel: {
        ...state.funnel,
        sections: state.funnel.sections.map((s) => ({
          ...s,
          columns: s.columns.map((c) => ({
            ...c,
            elements: c.elements.filter((e) => e.id !== elementId),
          })),
        })),
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
      selectedElementId: state.selectedElementId === elementId ? null : state.selectedElementId,
    }));
    get().saveToHistory();
  },

  duplicateElement: (elementId) => {
    const state = get();
    const found = findElement(state.funnel.sections, elementId);
    if (!found) return;

    const duplicatedElement: FunnelElement = {
      ...found.element,
      id: uuidv4(),
    } as FunnelElement;

    const sections = state.funnel.sections.map((s) =>
      s.id === found.sectionId
        ? {
            ...s,
            columns: s.columns.map((c) =>
              c.id === found.columnId
                ? {
                    ...c,
                    elements: [
                      ...c.elements.slice(
                        0,
                        c.elements.findIndex((e) => e.id === elementId) + 1
                      ),
                      duplicatedElement,
                      ...c.elements.slice(
                        c.elements.findIndex((e) => e.id === elementId) + 1
                      ),
                    ],
                  }
                : c
            ),
          }
        : s
    );

    set({
      funnel: {
        ...state.funnel,
        sections,
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    });
    get().saveToHistory();
  },

  updateElement: (elementId, updates) => {
    const state = get();
    const sections = state.funnel.sections.map((s) => ({
      ...s,
      columns: s.columns.map((c) => ({
        ...c,
        elements: c.elements.map((e) =>
          e.id === elementId ? ({ ...e, ...updates } as FunnelElement) : e
        ),
      })),
    }));

    set({
      funnel: {
        ...state.funnel,
        sections,
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    });
    get().saveToHistory();
  },

  updateElementStyles: (elementId, styles) => {
    set((state) => ({
      funnel: {
        ...state.funnel,
        sections: state.funnel.sections.map((s) => ({
          ...s,
          columns: s.columns.map((c) => ({
            ...c,
            elements: c.elements.map((e) =>
              e.id === elementId
                ? { ...e, styles: { ...e.styles, ...styles } }
                : e
            ),
          })),
        })),
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
  },

  updateElementResponsiveStyles: (elementId, breakpoint, styles) => {
    set((state) => ({
      funnel: {
        ...state.funnel,
        sections: state.funnel.sections.map((s) => ({
          ...s,
          columns: s.columns.map((c) => ({
            ...c,
            elements: c.elements.map((e) =>
              e.id === elementId
                ? {
                    ...e,
                    responsive: {
                      ...e.responsive,
                      [breakpoint]: {
                        ...e.responsive?.[breakpoint],
                        ...styles,
                      },
                    },
                  }
                : e
            ),
          })),
        })),
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
  },

  moveElement: (elementId, targetSectionId, targetColumnId, targetIndex) => {
    const state = get();
    const found = findElement(state.funnel.sections, elementId);
    if (!found) return;

    // Remove element from source
    let sections = state.funnel.sections.map((s) => ({
      ...s,
      columns: s.columns.map((c) => ({
        ...c,
        elements: c.elements.filter((e) => e.id !== elementId),
      })),
    }));

    // Add element to target
    sections = sections.map((s) =>
      s.id === targetSectionId
        ? {
            ...s,
            columns: s.columns.map((c) =>
              c.id === targetColumnId
                ? {
                    ...c,
                    elements: [
                      ...c.elements.slice(0, targetIndex),
                      found.element,
                      ...c.elements.slice(targetIndex),
                    ],
                  }
                : c
            ),
          }
        : s
    );

    set({
      funnel: {
        ...state.funnel,
        sections,
        metadata: {
          ...state.funnel.metadata,
          updatedAt: new Date().toISOString(),
        },
      },
    });
    get().saveToHistory();
  },

  // Selection actions
  selectElement: (elementId) => {
    set({ selectedElementId: elementId });
  },

  selectSection: (sectionId) => {
    set({ selectedSectionId: sectionId });
  },

  // Viewport actions
  setBreakpoint: (breakpoint) => {
    set({ currentBreakpoint: breakpoint });
  },

  // History actions
  saveToHistory: () => {
    set((state) => {
      const newHistory = state.history.slice(0, state.historyIndex + 1);
      newHistory.push(state.funnel);
      
      // Limit history to 50 items
      if (newHistory.length > 50) {
        newHistory.shift();
      }

      return {
        history: newHistory,
        historyIndex: newHistory.length - 1,
      };
    });
  },

  undo: () => {
    set((state) => {
      if (state.historyIndex > 0) {
        return {
          funnel: state.history[state.historyIndex - 1],
          historyIndex: state.historyIndex - 1,
        };
      }
      return state;
    });
  },

  redo: () => {
    set((state) => {
      if (state.historyIndex < state.history.length - 1) {
        return {
          funnel: state.history[state.historyIndex + 1],
          historyIndex: state.historyIndex + 1,
        };
      }
      return state;
    });
  },

  // Drag state
  setIsDragging: (isDragging) => {
    set({ isDragging });
  },

  // Preview mode
  setPreviewMode: (isPreview) => {
    set({ isPreviewMode: isPreview });
    // Clear selection when entering preview mode
    if (isPreview) {
      set({ selectedElementId: null, selectedSectionId: null });
    }
  },
}));

